import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Loader2, Crown, MapPin, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const INVESTMENT_RANGES = [
  "Below ₹10 Lakh",
  "₹10 Lakh – ₹25 Lakh",
  "₹25 Lakh – ₹50 Lakh",
  "₹50 Lakh – ₹1 Cr",
  "Above ₹1 Cr",
];

const CITIES = [
  "Mumbai", "Bengaluru", "Pune", "Hyderabad", "Chennai",
  "Kolkata", "Jaipur", "Lucknow", "Indore", "Surat", "Nagpur",
  "Coimbatore", "Kochi", "Vadodara", "Bhopal", "Ludhiana", "Gurgaon",
  "Noida", "Mohali", "Goa", "Other",
];

export default function Franchise() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city_of_interest: "Mumbai",
    current_occupation: "", investment_capacity: "₹25 Lakh – ₹50 Lakh", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/franchise", form);
      setDone(true);
      toast.success("Application received. We'll be in touch.");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="franchise-page" className="bg-white">
      {/* HERO */}
      <section className="bg-navy text-white py-24 lg:py-32 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)",
            backgroundSize: "cover",
          }}
        />
        <div className="container-narrow relative">
          <div className="overline text-gold mb-4">Franchise · India</div>
          <h1 className="text-4xl sm:text-6xl font-display font-semibold leading-[1.05] max-w-4xl">
            Lead with <span className="italic text-gold">INFLUENCE</span>. Build a network your city is missing.
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl">
            We're inviting selected business owners across India to launch and
            lead a D32 region. If you've built businesses and want to build a
            community of builders, read on.
          </p>
        </div>
      </section>

      {/* WHY */}
      <section className="py-24 lg:py-28">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              {
                icon: Crown,
                title: "An established model",
                body:
                  "15+ years of playbook from D32 Australia. Curriculum, events, tech stack, member journey, all ready.",
              },
              {
                icon: MapPin,
                title: "Exclusive city rights",
                body:
                  "One franchisee per city. Be the founding face of D32 in your market with full territorial protection.",
              },
              {
                icon: TrendingUp,
                title: "Multiple revenue streams",
                body:
                  "Membership, events, sponsorships, partnerships, a network business with high LTV and low overhead.",
              },
            ].map((b, i) => (
              <div
                key={b.title}
                className={`p-8 rounded-2xl border ${
                  i === 1
                    ? "bg-navy text-white border-navy"
                    : "bg-white border-gray-200"
                }`}
              >
                <b.icon
                  size={22}
                  className={i === 1 ? "text-gold" : "text-gold"}
                />
                <h3
                  className={`mt-5 font-display text-xl ${
                    i === 1 ? "text-white" : "text-navy"
                  }`}
                >
                  {b.title}
                </h3>
                <p
                  className={`mt-2 text-sm leading-relaxed ${
                    i === 1 ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-cream py-24 lg:py-28">
        <div className="container-narrow grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline text-gold mb-3">Apply</div>
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">
              Tell us who you are. We'll set up a call.
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              We're selective. Our typical franchise partner is an established
              business owner, ex-founder, or senior operator with a strong
              network in their city and a real desire to build community.
            </p>
            <div className="mt-8 space-y-4">
              {[
                "1:1 discovery call within 48 hours",
                "Territory & city potential review",
                "Pilot meeting in your city",
                "Onboarding & playbook handover",
              ].map((step, i) => (
                <div key={step} className="flex items-start gap-3">
                  <div className="font-display text-gold text-2xl leading-none">
                    {`0${i + 1}`.slice(-2)}
                  </div>
                  <div className="text-sm text-gray-700 pt-1">{step}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-gray-200 shadow-sm">
              {done ? (
                <div data-testid="franchise-form-success" className="py-16 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 flex items-center justify-center mb-6">
                    <Check size={28} className="text-navy" />
                  </div>
                  <h3 className="font-display text-3xl text-navy">Application received.</h3>
                  <p className="mt-3 text-gray-600 max-w-sm mx-auto">
                    Our franchise team will be in touch within 48 hours to
                    schedule a call.
                  </p>
                </div>
              ) : (
                <form
                  data-testid="franchise-form"
                  onSubmit={onSubmit}
                  className="space-y-5"
                >
                  <h3 className="font-display text-2xl text-navy">
                    Franchise Application
                  </h3>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fname">Full name *</Label>
                      <Input id="fname" required
                        data-testid="franchise-input-name"
                        value={form.name} onChange={update("name")}
                        className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <Label htmlFor="fphone">Phone *</Label>
                      <Input id="fphone" required
                        data-testid="franchise-input-phone"
                        value={form.phone} onChange={update("phone")}
                        className="mt-1.5 h-11" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="femail">Email *</Label>
                    <Input id="femail" type="email" required
                      data-testid="franchise-input-email"
                      value={form.email} onChange={update("email")}
                      className="mt-1.5 h-11" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fcity">City of interest *</Label>
                      <Select value={form.city_of_interest}
                        onValueChange={(v) => setForm((f) => ({ ...f, city_of_interest: v }))}>
                        <SelectTrigger id="fcity" data-testid="franchise-input-city" className="mt-1.5 h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="finv">Investment capacity *</Label>
                      <Select value={form.investment_capacity}
                        onValueChange={(v) => setForm((f) => ({ ...f, investment_capacity: v }))}>
                        <SelectTrigger id="finv" data-testid="franchise-input-investment" className="mt-1.5 h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INVESTMENT_RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="focc">Current occupation / business</Label>
                    <Input id="focc"
                      data-testid="franchise-input-occupation"
                      value={form.current_occupation} onChange={update("current_occupation")}
                      className="mt-1.5 h-11" />
                  </div>

                  <div>
                    <Label htmlFor="fmsg">Why D32? Tell us about your network</Label>
                    <Textarea id="fmsg" rows={4}
                      data-testid="franchise-input-message"
                      value={form.message} onChange={update("message")}
                      className="mt-1.5" />
                  </div>

                  {error && (
                    <div data-testid="franchise-form-error" className="text-sm text-red-600">{error}</div>
                  )}

                  <button
                    type="submit"
                    data-testid="franchise-form-submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gold text-white font-semibold py-4 rounded-full hover:-translate-y-0.5 transition disabled:opacity-60"
                  >
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                    Submit application
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
